import { request } from './http';
import type { CardBin, CardLifecycleStatus, CardSecret, VccCard } from '@/types/card';
import type { PaginatedResult } from '@/types/api';
import type { TransactionSyncResult } from '@/types/transaction';

export interface CardListQuery {
  status?: CardLifecycleStatus;
  bin?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
}

export interface CardCreateRequest {
  bin: string;
  alias: string;
  billingProfileId: string;
  /** 初始限额（整数美分） */
  totalLimitCents: number;
  /** 是否常驻可充值 */
  reloadable: boolean;
}

export interface CardFundsRequest {
  /** 调拨金额（整数美分，正数） */
  amountCents: number;
  /** 业务备注 */
  remark?: string;
}

export interface CardLimitUpdateRequest {
  /** 新的消费限额（整数美分，对齐上游 spend limitation） */
  totalLimitCents: number;
}

export const cardsApi = {
  /** 获取所有可用卡段 */
  listBins() {
    return request<CardBin[]>({ url: '/cards/bins', method: 'GET' });
  },
  /** 卡列表（分页） */
  list(query: CardListQuery = {}) {
    return request<PaginatedResult<VccCard>>({ url: '/cards', method: 'GET', params: query });
  },
  /** 单张卡详情 */
  detail(cardId: string) {
    return request<VccCard>({ url: `/cards/${cardId}`, method: 'GET' });
  },
  /** 申请开卡 */
  create(payload: CardCreateRequest) {
    return request<VccCard>({ url: '/cards', method: 'POST', data: payload });
  },
  /** 调整消费限额（上游 Update a card / amount） */
  updateLimit(cardId: string, payload: CardLimitUpdateRequest) {
    return request<VccCard>({
      url: `/cards/${cardId}/limit`,
      method: 'POST',
      data: payload,
    });
  },
  /** 充值（母账户 → 卡余额） */
  refill(cardId: string, payload: CardFundsRequest) {
    return request<{ orderNo: string }>({
      url: `/cards/${cardId}/refill`,
      method: 'POST',
      data: payload,
    });
  },
  /** 提现（卡余额 → 母账户） */
  withdraw(cardId: string, payload: CardFundsRequest) {
    return request<{ orderNo: string }>({
      url: `/cards/${cardId}/withdraw`,
      method: 'POST',
      data: payload,
    });
  },
  /** 销卡 */
  close(cardId: string) {
    return request<{ orderNo: string }>({
      url: `/cards/${cardId}/close`,
      method: 'POST',
    });
  },
  /** 获取明文卡号 / CVV（暂不要求二次验证） */
  revealSecret(cardId: string) {
    return request<CardSecret>({
      url: `/cards/${cardId}/secret`,
      method: 'POST',
    });
  },
  /** 向上游拉取该卡交易并落库（按 transaction_id 去重） */
  refreshTransactions(cardId: string) {
    return request<TransactionSyncResult>({
      url: `/cards/${cardId}/transactions/refresh`,
      method: 'POST',
    });
  },
  /** 调用上游 Retrieve a card（type=add_card）同步可用余额 */
  refreshBalance(cardId: string) {
    return request<VccCard>({
      url: `/cards/${cardId}/balance/refresh`,
      method: 'POST',
    });
  },
  /** 手续费试算 */
  feePreview(payload: { kind: 'REFILL' | 'CARD_OPEN'; amountCents: number }) {
    return request<FeePreviewResult>({
      url: '/cards/fee-preview',
      method: 'POST',
      data: payload,
    });
  },
};

export interface FeePreviewResult {
  grossCents: number;
  feeCents: number;
  netCents: number;
  cardOpenFeeCents: number;
  walletDebitCents: number;
  depositFeeRateBps: number;
}
